// src/hooks/PlanContext.js
// Provides plan tier and feature overrides to the entire app.
// Loaded once on mount alongside OrgSettingsContext.
// Super-admins can override features per org from BillingTab.

import { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../config/supabase";

export const PlanContext = createContext({
  planTier:        "professional",
  featureOverrides: [],
  loading:         true,
  refreshPlan:     () => {},
});

const SUPER_ADMIN_EMAIL = process.env.REACT_APP_SUPER_ADMIN_EMAIL || "";

/**
 * Wraps the app — provides planTier and featureOverrides from the organizations table.
 */
export function PlanProvider({ orgId, userEmail, userRole, children }) {
  const [planTier,         setPlanTier]         = useState("professional");
  const [featureOverrides, setFeatureOverrides] = useState([]);
  const [loading,          setLoading]          = useState(true);

  // Super admin: email match (legacy) OR role = 'superadmin' (preferred)
  const isSuperAdmin =
    (userRole === "superadmin") ||
    (userEmail && SUPER_ADMIN_EMAIL && userEmail.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase());

  async function loadPlan() {
    if (!orgId) { setLoading(false); return; }
    try {
      const { data, error } = await supabase
        .from("organizations")
        .select("subscription_tier, feature_overrides")
        .eq("id", orgId)
        .single();

      if (!error && data) {
        setPlanTier(data.subscription_tier || "professional");
        setFeatureOverrides(Array.isArray(data.feature_overrides) ? data.feature_overrides : []);
      }
    } catch (e) {
      console.error("PlanContext load error:", e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadPlan(); }, [orgId]);

  return (
    <PlanContext.Provider value={{
      planTier,
      featureOverrides,
      loading,
      isSuperAdmin,
      refreshPlan: loadPlan,
    }}>
      {children}
    </PlanContext.Provider>
  );
}

export function usePlanContext() {
  return useContext(PlanContext);
}
